/**
 * Digital Invoice (חשבונית דיגיטלית) Integration Service
 *
 * Constants and utilities for integrating with Digital Invoice.
 * Cloud Functions handle actual API communication (see /functions/digitalInvoiceIntegration.js)
 *
 * Values confirmed by Digital Invoice developer (March 2026).
 */

// ==============================================
// DOCUMENT TYPES (from Digital Invoice API)
// ==============================================

export const DI_DOCUMENT_TYPES = {
  TAX_INVOICE: 1,          // חשבונית מס
  RECEIPT: 2,              // קבלה
  TAX_INVOICE_RECEIPT: 3,  // חשבונית מס קבלה
  PROFORMA_INVOICE: 4,     // חשבונית פרופורמה
  ORDER: 5,                // הזמנה
  PROPOSAL: 6,             // הצעת מחיר
  SHIPPING: 7,             // תעודת משלוח
  CREDIT: 8,               // זיכוי
  REFUND_RECEIPT: 9,       // קבלה זיכוי
  DONATION_RECEIPT: 10,    // קבלה על תרומה
  DONATION_CANCEL: 11,     // ביטול תרומה
};

export const DI_DOCUMENT_LABELS = {
  [DI_DOCUMENT_TYPES.TAX_INVOICE]: "חשבונית מס",
  [DI_DOCUMENT_TYPES.RECEIPT]: "קבלה",
  [DI_DOCUMENT_TYPES.TAX_INVOICE_RECEIPT]: "חשבונית מס קבלה",
  [DI_DOCUMENT_TYPES.PROFORMA_INVOICE]: "חשבונית פרופורמה",
  [DI_DOCUMENT_TYPES.ORDER]: "הזמנה",
  [DI_DOCUMENT_TYPES.PROPOSAL]: "הצעת מחיר",
  [DI_DOCUMENT_TYPES.SHIPPING]: "תעודת משלוח",
  [DI_DOCUMENT_TYPES.CREDIT]: "זיכוי",
  [DI_DOCUMENT_TYPES.REFUND_RECEIPT]: "קבלה זיכוי",
  [DI_DOCUMENT_TYPES.DONATION_RECEIPT]: "קבלה על תרומה",
  [DI_DOCUMENT_TYPES.DONATION_CANCEL]: "ביטול תרומה",
};

// ==============================================
// PAYMENT TYPES (from Digital Invoice API)
// ==============================================

export const DI_PAYMENT_TYPES = {
  CASH: 1,             // מזומן
  CREDIT_CARD: 2,      // כרטיס אשראי
  CHECK: 3,            // צ'ק
  BANK_TRANSFER: 4,    // העברה בנקאית
  CREDIT_POINTS: 5,    // נקודות זיכוי
  PAYPAL: 6,           // פייפאל
  OTHER: 7,            // אחר
  WITHHOLDING_TAX: 8,  // ניכוי מס במקור
  BIT: 9,              // ביט
  PAYBOX: 10,          // פייבוקס
  STOCKS: 11,          // מניות
};

export const DI_PAYMENT_LABELS = {
  [DI_PAYMENT_TYPES.CASH]: "מזומן",
  [DI_PAYMENT_TYPES.CREDIT_CARD]: "כרטיס אשראי",
  [DI_PAYMENT_TYPES.CHECK]: "צ'ק",
  [DI_PAYMENT_TYPES.BANK_TRANSFER]: "העברה בנקאית",
  [DI_PAYMENT_TYPES.CREDIT_POINTS]: "נקודות זיכוי",
  [DI_PAYMENT_TYPES.PAYPAL]: "PayPal",
  [DI_PAYMENT_TYPES.OTHER]: "אחר",
  [DI_PAYMENT_TYPES.WITHHOLDING_TAX]: "ניכוי מס במקור",
  [DI_PAYMENT_TYPES.BIT]: "ביט",
  [DI_PAYMENT_TYPES.PAYBOX]: "פייבוקס",
  [DI_PAYMENT_TYPES.STOCKS]: "מניות",
};

// ==============================================
// COMPANY TYPES (maps to Taxes table TaxID)
// ==============================================

export const DI_COMPANY_TYPES = {
  AUTHORIZED_DEALER: 1,  // עוסק מורשה
  COMPANY: 2,            // חברה
  VAT_EXEMPTION: 3,      // עוסק פטור
  NGO: 4,                // עמותה
  PUBLIC_BENEFIT: 5,     // חברה לתועלת הציבור
  PUBLIC_ENDOWMENT: 6,   // הקדש ציבורי
  REGULAR_COMPANY: 7,    // חברה רגילה
};

export const DI_COMPANY_TYPE_LABELS = {
  [DI_COMPANY_TYPES.AUTHORIZED_DEALER]: "עוסק מורשה",
  [DI_COMPANY_TYPES.COMPANY]: "חברה",
  [DI_COMPANY_TYPES.VAT_EXEMPTION]: "עוסק פטור",
  [DI_COMPANY_TYPES.NGO]: "עמותה",
  [DI_COMPANY_TYPES.PUBLIC_BENEFIT]: "חברה לתועלת הציבור",
  [DI_COMPANY_TYPES.PUBLIC_ENDOWMENT]: "הקדש ציבורי",
  [DI_COMPANY_TYPES.REGULAR_COMPANY]: "חברה רגילה",
};

// ==============================================
// TAX TYPES
// ==============================================

export const DI_TAX_TYPES = {
  VAT_EXEMPT: 1,  // 0% מע"מ
  VAT_18: 2,      // 18% מע"מ
};

// ==============================================
// DEFAULTS
// ==============================================

export const DEFAULT_VAT_RATE = 18;

export const DEFAULT_DI_SETTINGS = {
  defaultDocumentType: DI_DOCUMENT_TYPES.TAX_INVOICE_RECEIPT,
  defaultPaymentType: DI_PAYMENT_TYPES.CASH,
};

// ==============================================
// UTILITY FUNCTIONS
// ==============================================

export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "₪0.00";
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: "ILS",
  }).format(amount);
};

/**
 * Document type options for select inputs (most common for clinics first)
 */
export const getDocumentTypeOptions = () => [
  { value: DI_DOCUMENT_TYPES.TAX_INVOICE_RECEIPT, label: "חשבונית מס קבלה" },
  { value: DI_DOCUMENT_TYPES.TAX_INVOICE, label: "חשבונית מס" },
  { value: DI_DOCUMENT_TYPES.RECEIPT, label: "קבלה" },
  { value: DI_DOCUMENT_TYPES.CREDIT, label: "זיכוי" },
  { value: DI_DOCUMENT_TYPES.REFUND_RECEIPT, label: "קבלה זיכוי" },
  { value: DI_DOCUMENT_TYPES.PROFORMA_INVOICE, label: "חשבונית פרופורמה" },
  { value: DI_DOCUMENT_TYPES.ORDER, label: "הזמנה" },
  { value: DI_DOCUMENT_TYPES.PROPOSAL, label: "הצעת מחיר" },
  { value: DI_DOCUMENT_TYPES.SHIPPING, label: "תעודת משלוח" },
  { value: DI_DOCUMENT_TYPES.DONATION_RECEIPT, label: "קבלה על תרומה" },
  { value: DI_DOCUMENT_TYPES.DONATION_CANCEL, label: "ביטול תרומה" },
];

/**
 * All document type options including "all" (for filters)
 */
export const getAllDocumentTypeOptions = () => [
  { value: 0, label: "הכל" },
  ...getDocumentTypeOptions(),
];

export const getPaymentTypeOptions = () => [
  { value: DI_PAYMENT_TYPES.CASH, label: "מזומן" },
  { value: DI_PAYMENT_TYPES.CREDIT_CARD, label: "כרטיס אשראי" },
  { value: DI_PAYMENT_TYPES.BANK_TRANSFER, label: "העברה בנקאית" },
  { value: DI_PAYMENT_TYPES.CHECK, label: "צ'ק" },
  { value: DI_PAYMENT_TYPES.BIT, label: "ביט" },
  { value: DI_PAYMENT_TYPES.PAYBOX, label: "פייבוקס" },
  { value: DI_PAYMENT_TYPES.OTHER, label: "אחר" },
];

export const getCompanyTypeOptions = () => [
  { value: DI_COMPANY_TYPES.AUTHORIZED_DEALER, label: "עוסק מורשה" },
  { value: DI_COMPANY_TYPES.VAT_EXEMPTION, label: "עוסק פטור" },
  { value: DI_COMPANY_TYPES.COMPANY, label: "חברה" },
  { value: DI_COMPANY_TYPES.REGULAR_COMPANY, label: "חברה רגילה" },
  { value: DI_COMPANY_TYPES.NGO, label: "עמותה" },
  { value: DI_COMPANY_TYPES.PUBLIC_BENEFIT, label: "חברה לתועלת הציבור" },
  { value: DI_COMPANY_TYPES.PUBLIC_ENDOWMENT, label: "הקדש ציבורי" },
];

/**
 * Maps CliniKit payment method to Digital Invoice payment type
 */
export const mapPaymentMethodToDI = (paymentMethod) => {
  const mapping = {
    cash: DI_PAYMENT_TYPES.CASH,
    credit: DI_PAYMENT_TYPES.CREDIT_CARD,
    bit: DI_PAYMENT_TYPES.BIT,
    transfer: DI_PAYMENT_TYPES.BANK_TRANSFER,
    check: DI_PAYMENT_TYPES.CHECK,
    other: DI_PAYMENT_TYPES.OTHER,
  };
  return mapping[paymentMethod] || DI_PAYMENT_TYPES.OTHER;
};
